import { mainButton, miniApp, themeParams, type RGB } from '@tma.js/sdk-react'
import { useEffect } from 'react'
import { readColorToken } from './css-color'
import { refreshSecondaryButtonColors, setSecondaryButtonSurface } from './secondary-button'

/** Явные цвета интерфейса Telegram — при ручной теме (`data-theme`). */
export type ChromeColors = {
  /** Шапка Mini App — `tg-header-bg`. */
  header: RGB
  /** Фон под контентом (резиновый скролл) — фон приложения `tg-secondary-bg`. */
  background: RGB
  /** Нижняя панель с кнопками — `tg-bg`. */
  bottomBar: RGB
  button: RGB
  buttonText: RGB
}

/** Цвета интерфейса — именами CSS-токенов (`--color-qr-bg-from`), а не значениями. */
export type ChromeTokens = Record<keyof ChromeColors, `--${string}`>

/** Что выбрала тема: палитра клиента или явные цвета ручной темы. */
let synced: ChromeColors | 'system' = 'system'
/** Цвета экрана поверх темы (QR-экран 16), пока он открыт. */
let override: ChromeColors | null = null

const apply = (): void => {
  const colors = override ?? synced

  if (colors === 'system') {
    miniApp.setHeaderColor.ifAvailable(themeParams.headerBgColor() ? 'header_bg_color' : 'secondary_bg_color')
    miniApp.setBgColor.ifAvailable('secondary_bg_color')
    miniApp.setBottomBarColor.ifAvailable('bg_color')

    const button = themeParams.buttonColor()
    const buttonText = themeParams.buttonTextColor()

    if (button && buttonText) {
      mainButton.setParams.ifAvailable({ bgColor: button, textColor: buttonText })
    }
  } else {
    miniApp.setHeaderColor.ifAvailable(colors.header)
    miniApp.setBgColor.ifAvailable(colors.background)
    miniApp.setBottomBarColor.ifAvailable(colors.bottomBar)
    mainButton.setParams.ifAvailable({ bgColor: colors.button, textColor: colors.buttonText })
  }

  // Фон SecondaryButton смешивается с нижней панелью: у темы это `tg-bg`, у экрана со своими цветами — его панель.
  setSecondaryButtonSurface(override?.bottomBar ?? null)
  refreshSecondaryButtonColors()
}

/**
 * Перекрасить интерфейс Telegram заново — на `themeChanged` клиента
 * (`bindColorScheme`). Шапку и панели по ключам темы SDK перекрашивает сам,
 * а MainButton и SecondaryButton держат явные цвета — без этого после смены
 * темы клиента они остались бы в старой палитре. При ручной теме и на экране
 * со своими цветами повторяет те же явные цвета.
 */
export const refreshChromeColors = (): void => apply()

/**
 * Цвета интерфейса самого Telegram: шапка, фон под контентом, нижняя панель,
 * главная кнопка — по раскладке макета: шапка `tg-header-bg`, фон приложения
 * `tg-secondary-bg`, нижняя панель `tg-bg`.
 *
 * `system` — палитра клиента: цвета задаются ключами темы, SDK сам
 * перекрашивает их на `themeChanged`. У старого клиента без
 * `header_bg_color` шапка — цвета фона приложения. Явные цвета — при ручном
 * выборе темы, иначе экран светлый, а шапка, панели и кнопки остаются в палитре клиента.
 *
 * Пока экран держит свои цвета (`useChromeOverride`), смена темы только
 * запоминается и применяется после него.
 */
export const syncChromeColors = (colors: ChromeColors | 'system'): void => {
  synced = colors
  apply()
}

/**
 * Свои цвета шапки, нижней панели и MainButton, пока экран смонтирован, —
 * QR-экран (16): синий градиент `qr-bg-*` до краёв и белая «Готово». Цвета —
 * токенами стилей: значения живут в одном месте, в `app/styles`. Токен не
 * прочитался (не `#rrggbb`) — экран остаётся в цветах темы, без падения.
 * После экрана — снова цвета темы.
 */
export const useChromeOverride = (tokens: ChromeTokens | null): void => {
  // Эффект зависит от набора токенов, а не от нового объекта на каждый рендер.
  const key = tokens ? JSON.stringify(tokens) : null

  useEffect(() => {
    if (!key) {
      return
    }

    const entries = Object.entries(JSON.parse(key) as ChromeTokens).map(([part, token]) => [part, readColorToken(token)] as const)

    if (entries.some(([, color]) => color === null)) {
      return
    }

    override = Object.fromEntries(entries) as ChromeColors
    apply()

    return () => {
      override = null
      apply()
    }
  }, [key])
}
